import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DecisionGraph, JdmConfigProvider } from '@gorules/jdm-editor';
import type { DecisionGraphType, DecisionGraphRef } from '@gorules/jdm-editor';
import '@gorules/jdm-editor/dist/style.css';
import { Rule } from '../types';
import api from '../api';
import { useAuthContext } from '../App';

// ─── default simulator input ─────────────────────────────────────────────────
const DEFAULT_SIM_INPUT = {
  productType: 'Ground up (Sale or Rent)',
  loanProgram: 'Purchase',
  state: 'CA',
  existingPropertyType: 'Single Family Residence',
  purchasePrice: 200000,
  constructionHoldback: 350000,
  estimatedARV: 600000,
  estimatedAIV: 200000,
  loanAmount: 425000,
  termMonths: 12,
  interestRate: 0.095,
  avgFICO: 720,
  yearsExperience: 6,
  originationFeePercent: 2,
  uwFee: 999,
  drawFeePerDraw: 500,
  drawCount: 1,
  oddDaysCount: 0,
};

const SCENARIOS = [
  {
    label: 'Clean CA Ground Up (Tier1)',
    data: { ...DEFAULT_SIM_INPUT }
  },
  {
    label: 'NY → State Ineligible',
    data: { ...DEFAULT_SIM_INPUT, state: 'NY', loanAmount: 400000 }
  },
  {
    label: 'Short Term Rental blocked',
    data: { ...DEFAULT_SIM_INPUT, state: 'FL', productType: 'Rental Hold', existingPropertyType: 'Rental Hold - STVR', loanAmount: 400000, constructionHoldback: 250000, estimatedARV: 520000 }
  },
  {
    label: 'Term Exceeds Max',
    data: { ...DEFAULT_SIM_INPUT, termMonths: 24 }
  },
  {
    label: 'Low FICO → Ineligible',
    data: { ...DEFAULT_SIM_INPUT, avgFICO: 640, yearsExperience: 1, loanAmount: 380000, purchasePrice: 190000, constructionHoldback: 200000, estimatedARV: 480000, estimatedAIV: 190000 }
  },
  {
    label: 'LTC Exception (TX)',
    data: { ...DEFAULT_SIM_INPUT, state: 'TX', loanAmount: 550000 }
  },
];

// ─── severity config ──────────────────────────────────────────────────────────
const SEV: Record<string, string> = {
  CRITICAL: 'bg-red-100 border-red-300 text-red-800',
  HIGH: 'bg-orange-100 border-orange-300 text-orange-800',
  MEDIUM: 'bg-yellow-100 border-yellow-300 text-yellow-800',
};

function fmt(n: number | null | undefined, pct = false) {
  if (n == null) return '—';
  return pct ? (n * 100).toFixed(1) + '%' : n.toFixed(2);
}
function money(n: number | null | undefined) {
  if (n == null) return '—';
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ─── component ────────────────────────────────────────────────────────────────
export default function AdminRules() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const graphRef = useRef<DecisionGraphRef>(null);

  const [rule, setRule] = useState<Rule | null>(null);
  const [graph, setGraph] = useState<DecisionGraphType>({ nodes: [], edges: [] });
  const [graphReady, setGraphReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null);

  // simulator
  const [simInput, setSimInput] = useState(JSON.stringify(DEFAULT_SIM_INPUT, null, 2));
  const [simResult, setSimResult] = useState<any>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  // ── load active rule ──────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/pricing/rules/active')
      .then(res => {
        if (res.data?.jdmContent) {
          setRule(res.data);
          const { nodes, edges } = res.data.jdmContent as any;
          setGraph({ nodes: nodes ?? [], edges: edges ?? [] });
          setGraphReady(true);
        }
      })
      .catch(() => flash(false, 'Failed to load rules'))
      .finally(() => setLoading(false));
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────
  const flash = (ok: boolean, msg: string) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const handleChange = useCallback((val: DecisionGraphType) => {
    setGraph(val);
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!rule || !graph) return;
    setSaving(true);
    try {
      const payload = { contentType: 'application/vnd.gorules.decision', ...graph };
      const res = await api.put(`/pricing/rules/${rule.id}`, payload);
      setRule(res.data);
      setDirty(false);
      flash(true, `Saved v${res.data.version} — rules are live`);
    } catch (err: any) {
      flash(false, err.response?.data?.error ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── simulator ─────────────────────────────────────────────────────────────
  const loadScenario = (scenario: typeof SCENARIOS[0]) => {
    setSimInput(JSON.stringify(scenario.data, null, 2));
    setSimResult(null);
    setSimError(null);
  };

  const runSim = async () => {
    setSimLoading(true);
    setSimResult(null);
    setSimError(null);
    try {
      const input = JSON.parse(simInput);
      const res = await api.post('/pricing/evaluate', input);
      setSimResult(res.data.data);
    } catch (err: any) {
      setSimError(err.response?.data?.error ?? err.message ?? 'Evaluation failed');
    } finally {
      setSimLoading(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">

      {/* ── top bar ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#1A1D27] border-b border-gray-700 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            ← Dashboard
          </button>
          <div className="w-px h-4 bg-gray-700" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#2563EB] rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold font-mono">GC</span>
            </div>
            <span className="text-white font-semibold text-sm">Rule Editor</span>
          </div>
          {rule && (
            <span className="text-xs font-mono text-[#60A5FA] bg-[#2563EB]/10 px-2 py-0.5 rounded border border-[#2563EB]/20">
              v{rule.version}
            </span>
          )}
          {dirty && (
            <span className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-0.5 rounded border border-[#F59E0B]/20">
              Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {user?.name} · Reviewer
          </span>
          <button
            onClick={handleSave}
            disabled={saving || !dirty || !rule}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            {saving ? (
              <><span className="animate-spin text-base">⟳</span> Saving...</>
            ) : (
              <>💾 Save &amp; Deploy</>
            )}
          </button>
        </div>
      </div>

      {/* ── toast ── */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm font-medium shadow-xl transition-all ${toast.ok ? 'bg-[#10B981] text-white' : 'bg-[#EF4444] text-white'}`}>
          {toast.ok ? '✅' : '❌'} {toast.msg}
        </div>
      )}

      {/* ── main split ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT: visual graph editor ── */}
        <div className="flex-1 overflow-hidden border-r border-gray-200">
          {loading || !graphReady ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <div className="text-2xl mb-2">⟳</div>
                <div className="text-sm">Loading rule graph...</div>
              </div>
            </div>
          ) : (
            <JdmConfigProvider>
              <div style={{ width: '100%', height: '100%' }}>
                <DecisionGraph
                  ref={graphRef}
                  value={graph}
                  onChange={handleChange}
                  mode="business"
                />
              </div>
            </JdmConfigProvider>
          )}
        </div>

        {/* ── RIGHT: simulator panel ── */}
        <div className="w-[380px] shrink-0 flex flex-col bg-[#0F1117] overflow-hidden">

          {/* simulator header */}
          <div className="px-4 py-3 border-b border-gray-800">
            <div className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-1">
              Rule Simulator
            </div>
            <div className="text-xs text-gray-600">
              Test any loan against the current rules
            </div>
          </div>

          {/* scenario quick-pick */}
          <div className="px-4 py-3 border-b border-gray-800">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Quick Scenarios</div>
            <div className="flex flex-wrap gap-1.5">
              {SCENARIOS.map(s => (
                <button
                  key={s.label}
                  onClick={() => loadScenario(s)}
                  className="text-xs px-2 py-1 rounded bg-[#1A1D27] hover:bg-[#252836] border border-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* input textarea */}
          <div className="px-4 py-3 flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Loan Input (JSON)</div>
            <textarea
              value={simInput}
              onChange={e => { setSimInput(e.target.value); setSimResult(null); setSimError(null); }}
              className="flex-1 bg-[#1A1D27] text-[#E2E8F0] font-mono text-xs p-3 rounded-lg border border-gray-800 resize-none focus:outline-none focus:border-[#2563EB] min-h-0"
              spellCheck={false}
            />
            <button
              onClick={runSim}
              disabled={simLoading}
              className="mt-3 w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
            >
              {simLoading ? '⟳ Running...' : '▶ Run Evaluation'}
            </button>
          </div>

          {/* result area */}
          <div className="border-t border-gray-800 overflow-y-auto" style={{ maxHeight: '55%' }}>
            {simError && (
              <div className="m-4 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-3 text-xs text-[#F87171] font-mono">
                {simError}
              </div>
            )}

            {simResult && !simError && (
              <div className="p-4 space-y-3">

                {/* Tier */}
                <div className="bg-[#1A1D27] rounded-lg p-3 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1.5">Borrower Tier</div>
                  <span className={`font-mono text-sm font-bold px-2 py-1 rounded ${
                    simResult.borrowerTier === 'Tier1' ? 'bg-[#10B981]/20 text-[#34D399]' :
                    simResult.borrowerTier === 'Tier2' ? 'bg-[#F59E0B]/20 text-[#FBBF24]' :
                    'bg-[#EF4444]/20 text-[#F87171]'
                  }`}>
                    {simResult.borrowerTier ?? '—'}
                  </span>
                </div>

                {/* Ratios */}
                <div className="bg-[#1A1D27] rounded-lg p-3 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Ratios</div>
                  <div className="space-y-1.5 text-xs font-mono">
                    {simResult.ltc != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">LTC</span>
                        <span className={simResult.ltcBreached ? 'text-[#EF4444] font-bold' : 'text-white'}>
                          {fmt(simResult.ltc, true)} {simResult.ltcBreached ? '⚠' : ''} / max {fmt(simResult.maxLTC, true)}
                        </span>
                      </div>
                    )}
                    {simResult.ltarv != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">LTARV</span>
                        <span className={simResult.ltarvBreached ? 'text-[#EF4444] font-bold' : 'text-white'}>
                          {fmt(simResult.ltarv, true)} {simResult.ltarvBreached ? '⚠' : ''} / max {fmt(simResult.maxLTARV, true)}
                        </span>
                      </div>
                    )}
                    {simResult.ltv != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">LTV</span>
                        <span className="text-white">{fmt(simResult.ltv, true)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-[#1A1D27] rounded-lg p-3 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Pricing</div>
                  <div className="space-y-1.5 text-xs font-mono">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Base Rate</span>
                      <span className="text-white">{fmt(simResult.baseRate)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#60A5FA] font-bold">Final Rate</span>
                      <span className="text-[#60A5FA] font-bold">{fmt(simResult.finalRate)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max Loan</span>
                      <span className={simResult.loanExceedsMax ? 'text-[#EF4444]' : 'text-white'}>
                        {money(simResult.maxAllowedLoan)} {simResult.loanExceedsMax ? '⚠' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exceptions */}
                <div className="bg-[#1A1D27] rounded-lg p-3 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                    Exceptions ({simResult.exceptions?.length ?? 0})
                  </div>
                  {simResult.exceptions?.length === 0 ? (
                    <div className="text-xs text-[#10B981] flex items-center gap-1.5">
                      <span>✓</span> No exceptions — deal is clean
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {simResult.exceptions?.map((ex: any, i: number) => (
                        <div
                          key={i}
                          className={`rounded px-2 py-1.5 border text-xs ${SEV[ex.severity] ?? SEV.HIGH}`}
                        >
                          <div className="font-semibold font-mono">{ex.severity} — {ex.exceptionCode}</div>
                          <div className="mt-0.5 opacity-80">{ex.exceptionMessage}</div>
                          <div className="mt-0.5 opacity-70 italic">→ {ex.action || ex.requiredAction}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}

            {!simResult && !simError && !simLoading && (
              <div className="p-4 text-center text-xs text-gray-600">
                Pick a scenario or paste loan JSON above, then click Run
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
